import React, { useState } from 'react';
import Button from '../Button/Button';
import { t, toEnglish } from '../../i18n/i18n';
import './FilterDropdown.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/** Options come as plain strings or as `{ label, value }` pairs. */
const normalise = (option) =>
  option !== null && typeof option === 'object'
    ? { label: option.label ?? option.value, value: option.value }
    : { label: option, value: option };

/** A filter resting on its "everything" option shows the generic label instead. */
const ALL_LABELS = new Set([
  'all',
  'all sizes',
  'all accounts',
  'all types',
  'all statuses',
  'all certificates',
]);

export default function FilterDropdown({
  options = ['All Accounts', 'Evaluation', 'Funded'],
  value = 'All Accounts',
  onChange,
  label = 'Filters',
  className = '',
  /**
   * Which way the menu opens. A toolbar sits at the top of its card and drops
   * `down`; a control in the pager sits at the bottom, where `down` would open
   * off the end of the card.
   */
  placement = 'down',
  /** Which edge of the trigger the menu lines up with. */
  align = 'right',
  /** The trigger's glyph — the sort arrows suit a filter, a chevron a value. */
  icon: iconName = 'user-updown',
}) {
  const [isOpen, setIsOpen] = useState(false);

  const items = options.map(normalise);

  const handleSelect = (next) => {
    if (onChange) {
      onChange(next);
    }
    setIsOpen(false);
  };

  // Display the selected option's label on the button unless it is the "All" one
  const selected = items.find((item) => item.value === value);
  const displayLabel =
    selected && !ALL_LABELS.has(String(toEnglish(selected.label)).toLowerCase())
      ? t(toEnglish(selected.label))
      : t(toEnglish(label));

  return (
    <div className={`filter-dropdown-wrapper ${isOpen ? 'ac-menu-open is-open' : ''} ${className}`.trim()}>
      <Button
        variant="toolbar"
        iconPlacement="end"
        icon={<span className="icon" style={icon(iconName)} />}
        onClick={() => setIsOpen(!isOpen)}
        className={isOpen ? 'is-active' : ''}
      >
        {displayLabel}
      </Button>

      {isOpen && (
        <div
          className="filter-dropdown-backdrop"
          onClick={() => setIsOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99,
            background: 'transparent',
          }}
        />
      )}

      <div
        className={`filter-dropdown-menu is-${placement} is-align-${align} ${
          isOpen ? 'is-open' : ''
        }`}
      >
        {items.map((item) => {
          const isSelected = item.value === value;
          return (
            <button
              key={String(item.value)}
              type="button"
              className={`filter-dropdown-item ${isSelected ? 'is-selected' : ''}`}
              onClick={() => handleSelect(item.value)}
            >
              <span className="filter-dropdown-item-label">{t(toEnglish(item.label))}</span>
              {isSelected && (
                <span className="icon filter-dropdown-item-check" style={icon('check-sm')} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
